import { motion } from 'framer-motion'
import HanDuShell, { HanDuSection } from './HanDuShell'
import HanDuLogo from './HanDuLogo'
import MiniReader from './MiniReader'
import { fadeUp, stagger, viewportOnce } from '../../motion'
import { appStore, demoLibraryCards, faq, features } from '../data'

export default function Landing() {
  return (
    <HanDuShell>
      <Hero />

      <HanDuSection
        id="reader"
        eyebrow="The reader"
        title="Read first. Look things up without stopping."
      >
        <motion.div variants={fadeUp}>
          <MiniReader />
        </motion.div>
      </HanDuSection>

      <HanDuSection id="features" eyebrow="What it does" title="Everything in the app">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((feature) => (
            <motion.div
              key={feature.title}
              variants={fadeUp}
              className="handu-panel p-6"
            >
              <h3 className="text-lg font-bold">{feature.title}</h3>
              <p className="mt-2 leading-relaxed text-handu-soft">{feature.body}</p>
            </motion.div>
          ))}
        </div>
      </HanDuSection>

      <HanDuSection
        id="library"
        eyebrow="The library"
        title="Texts categorized so you can find your level and interest"
      >
        <motion.p variants={fadeUp} className="mb-8 max-w-2xl leading-relaxed text-handu-soft">
          Every text carries a level and a topic, and the library filters on both.
          Finish one and it drops to the bottom of the list, so what you have not
          read yet is always what you see first.
        </motion.p>
        <div className="grid max-w-3xl gap-4 sm:grid-cols-2">
          {demoLibraryCards.map((card) => (
            <motion.div key={card.title} variants={fadeUp} className="handu-panel p-5">
              <LevelBadge level={card.level} />
              <h3 className="mt-3 text-xl font-bold leading-snug">{card.title}</h3>
              <p className="mt-1 italic text-handu-soft">{card.description}</p>
              <p className="mt-4 text-sm text-handu-soft">◇ {card.category}</p>
            </motion.div>
          ))}
        </div>
      </HanDuSection>

      <HanDuSection id="faq" eyebrow="Questions" title="Before you start">
        <dl className="max-w-3xl space-y-4">
          {faq.map((item) => (
            <motion.div key={item.q} variants={fadeUp} className="handu-panel p-6">
              <dt className="text-lg font-bold">{item.q}</dt>
              <dd className="mt-2 leading-relaxed text-handu-soft">{item.a}</dd>
            </motion.div>
          ))}
        </dl>
      </HanDuSection>
    </HanDuShell>
  )
}

function Hero() {
  return (
    <section className="mx-auto max-w-content px-5 pb-6 pt-16 sm:px-8 sm:pt-24">
      <motion.div
        variants={stagger}
        initial="hidden"
        animate="show"
        className="flex flex-col items-center text-center"
      >
        <motion.div variants={fadeUp}>
          <HanDuLogo size={92} />
        </motion.div>

        <motion.h1
          variants={fadeUp}
          className="mt-7 text-4xl font-bold tracking-tight sm:text-5xl"
        >
          <span className="hanzi">汉读</span> HanDu
        </motion.h1>

        <motion.p
          variants={fadeUp}
          className="mt-4 max-w-xl text-lg leading-relaxed text-handu-soft sm:text-xl"
        >
          Read real Chinese, and tap anything you don&apos;t know. A character gives you
          its pinyin and meaning; a sentence gives you the whole line. No flashcards,
          no drills — just reading, with the dictionary already in the page.
        </motion.p>

        <motion.div variants={fadeUp} className="mt-8">
          <AppStoreBadge />
        </motion.div>

        <motion.p variants={fadeUp} className="mt-4 text-sm text-handu-soft/80">
          iPhone · Free · Beginner to advanced
        </motion.p>
      </motion.div>
    </section>
  )
}

// Renders a link once appStore.url is set in data.js, and a static badge
// until then — so publishing is a one-line change rather than a rewrite.
function AppStoreBadge() {
  const classes =
    'inline-flex items-center gap-2.5 rounded-full border-2 border-handu-gold bg-handu-container px-7 py-3.5 text-base font-semibold text-white shadow-[0_4px_14px_rgba(192,57,43,0.28)]'

  if (appStore.available && appStore.url) {
    return (
      <a href={appStore.url} className={`${classes} transition-transform hover:-translate-y-0.5`}>
        Download on the App Store
        <span aria-hidden="true">→</span>
      </a>
    )
  }

  return (
    <span className={classes}>
      <span aria-hidden="true"></span>
      {appStore.label}
    </span>
  )
}

function LevelBadge({ level }) {
  // Colours and the light/dark text pairing come from the app's badge
  // colorsets — beginner is a light gold that needs dark text, intermediate
  // a deep teal that needs white.
  const styles = {
    Beginner: 'bg-handu-beginner text-handu-ink',
    Intermediate: 'bg-handu-intermediate text-white',
    Advanced: 'bg-handu-primary text-white',
  }
  return (
    <span
      className={`inline-block rounded-md px-2.5 py-1 text-xs font-medium ${styles[level]}`}
    >
      {level}
    </span>
  )
}
